import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { downloadThyroidPdf } from "../../utils/generatePdf";
import { 
  ArrowLeft, Download, Printer, Share2, Check, 
  AlertCircle, FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { useAuth, API } from "../../App";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import BrandLogo from "../../components/BrandLogo";

// Module-level markdown components (stable identity — avoids React re-creating the
// component type on every parent render, which would tear down the markdown subtree).
const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-slate-900 mt-6 mb-3">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-slate-900 mt-5 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">{children}</h3>,
  p: ({ children }) => <p className="text-slate-700 text-sm leading-relaxed mb-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 mb-3">{children}</ul>,
  li: ({ children }) => <li className="text-slate-700 text-sm">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="text-slate-600">{children}</em>,
};

export default function MobileResults() {
  const { diagnosisId } = useParams();
  const id = diagnosisId; // alias for consistency
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  // Email consultation removed
  const resultRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDiagnosis();
    // fetchDiagnosis is intentionally not listed — it's a stable inline async closure
    // and listing it would create an unstable dep that re-runs on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDiagnosis = async () => {
    try {
      const response = await axios.get(`${API}/diagnosis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnosis(response.data);

      // If paid but not analyzed, trigger analysis
      if (response.data.status === "paid" && !response.data.result) {
        await runAnalysis();
      }
      // If analysis is in progress, start polling
      else if (response.data.status === "analyzing") {
        setAnalyzing(true);
        pollForResult();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load diagnosis");
    }
    setLoading(false);
  };

  const pollForResult = async () => {
    const maxPolls = 60; // 60 * 3s = 3 minutes max
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        const response = await axios.get(`${API}/diagnosis/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDiagnosis(prev => ({ ...prev, analysis_stage: response.data.analysis_stage }));
        if (response.data.status === "analyzed" && response.data.result) {
          setDiagnosis(response.data);
          setAnalyzing(false);
          return;
        }
        if (response.data.status === "error") {
          setError(response.data.error_detail || "Analysis failed");
          setAnalyzing(false);
          return;
        }
      } catch (err) {
        // Continue polling on transient errors but log for observability
        console.warn("[MobileResults] Transient error while polling diagnosis:", err?.message || err);
      }
    }
    setError("Analysis timed out. Please try again.");
    setAnalyzing(false);
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/diagnosis/${id}/analyze`, {
        language: i18n.language
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // If already analyzed, show result immediately
      if (response.data.status === "analyzed" && response.data.result) {
        setDiagnosis(prev => ({ ...prev, result: response.data.result, status: "analyzed" }));
        setAnalyzing(false);
        return;
      }
      // Otherwise poll for result
      pollForResult();
    } catch (err) {
      setError(err.response?.data?.detail || "Analysis failed");
      setAnalyzing(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!diagnosis) return;
    try {
      await downloadThyroidPdf(diagnosis, null, i18n);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('results.shareTitle') || 'Thyroid Analysis Results',
          text: t('results.shareText') || 'View my thyroid analysis results',
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-3 border-sky-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (analyzing) {
    const stageOrder = ['analyzing_labs', 'generating_recommendations', 'preparing_report'];
    const currentStage = diagnosis?.analysis_stage;
    const currentIdx = stageOrder.indexOf(currentStage);
    const stages = [
      { key: 'analyzing_labs', label: t('results.stageAnalyzingLabs') },
      { key: 'generating_recommendations', label: t('results.stageGeneratingRecs') },
      { key: 'preparing_report', label: t('results.stagePreparingReport') }
    ];
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-0 shadow-xl">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="animate-spin w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full"></div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">{t('results.analyzing')}</h2>
            <div className="space-y-3 text-left">
              {stages.map((stage, idx) => {
                const isCompleted = idx < currentIdx;
                const isActive = idx === currentIdx;
                return (
                  <div key={stage.key} className={`flex items-center gap-3 transition-all duration-500 ${isActive ? 'text-sky-600 font-medium' : isCompleted ? 'text-emerald-600' : 'text-slate-300'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-emerald-100' : isActive ? 'bg-sky-100' : 'bg-slate-100'}`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4" />
                      ) : isActive ? (
                        <div className="animate-spin w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                      )}
                    </div>
                    <span className="text-sm">{stage.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-0 shadow-xl">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('errors.analysisFailed')}</h2>
            <p className="text-slate-500 text-sm mb-4">{t('errors.analysisTryAgain')}</p>
            <div className="space-y-2">
              <Button onClick={() => { setError(""); runAnalysis(); }} className="w-full" data-testid="retry-analysis-btn">
                {t('results.generateAnalysis')}
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">
                {t('common.back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email Modal removed

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-slate-600">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900">{t('results.title')}</h1>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Results Content */}
      <main className="p-4 pb-32">
        {diagnosis?.result ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div ref={resultRef} className="prose prose-sm max-w-none">
                <div className="text-center mb-6 print:mb-4">
                  <div className="flex items-center justify-center mb-2">
                    <BrandLogo variant="header" showText={false} />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 mb-1">{t('landing.title')}</h2>
                  <p className="text-xs text-slate-500">
                    {new Date(diagnosis.created_at).toLocaleDateString()}
                  </p>
                </div>
                <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                  {diagnosis.result}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t('results.noResults')}</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Fixed Bottom Actions */}
      {diagnosis?.result && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 safe-area-inset-bottom">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              className="h-12 rounded-xl"
              data-testid="mobile-download-pdf"
            >
              <Download className="w-5 h-5 mr-2" />
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              className="h-12 rounded-xl"
              data-testid="mobile-print"
            >
              <Printer className="w-5 h-5 mr-2" />
              {t('results.print')}
            </Button>
          </div>
          {navigator.share && (
            <Button
              variant="outline"
              onClick={handleShare}
              className="w-full h-12 rounded-xl"
              data-testid="mobile-share"
            >
              <Share2 className="w-5 h-5 mr-2" />
              {t('common.share') || 'Share'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
